import { Injectable } from '@angular/core';
import { MessageService } from '@sla-ng/core';
import { Moment } from 'moment';
import { BehaviorSubject, finalize, firstValueFrom, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IAddCostAccounting } from '../../../models/cost-accounting-add.model';
import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { ICostAccounting } from '../../../models/cost-accounting.model';
import { ICostTypeCost } from '../../../models/cost-type-cost.model';
import { ICalculationResult } from '../../../models/calculation-result.model';
import { IErrorCheck } from '../../../models/error-check.model';
import { IFallbackOptions } from '../../../models/fallback-options.model';
import { IIndirectCostImport } from '../../../models/indirect-cost-import.model';
import { CostAccountingTypeResource } from '../../../resources/cost-accounting-type.resource';
import { CostAccountingResource } from '../../../resources/cost-accounting.resource';
import { ICostAccountingState } from './cost-accounting-state.model';

let _state: ICostAccountingState = {
    costAccountingTypes: [],
    isLoading: false,
    data: [],
    costTypeCosts: [],
};

@Injectable({
    providedIn: 'root',
})
export class CostAccountingFacade {
    protected store$: BehaviorSubject<ICostAccountingState> = new BehaviorSubject<ICostAccountingState>(_state);

    constructor(private myCostAccountingResource: CostAccountingResource,
                private myCostAccountingTypeResource: CostAccountingTypeResource,
                private myMessageService: MessageService) {
    }

    public getStateSnapshot(): ICostAccountingState {
        return this.store$.value;
    }

    public subState(): Observable<ICostAccountingState> {
        return this.store$.asObservable();
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);
        await Promise.all([
            this.fetchCostAccountingTypes(),
            firstValueFrom(this.fetchCostAccountings()),
        ]);
        this.setIsloading(false);
    }

    public clearState(): void {
        this.updateState({
            costAccountingTypes: [],
            isLoading: false,
            data: _state.data,
            costTypeCosts: [],
        });
    }

    public fetchCostAccountings(costAccountingTypeId?: number, from?: Moment, to?: Moment, forceReload?: boolean): Observable<ICostAccounting[]> {
        if (_state.data.length > 0 && !forceReload) {
            return of(_state.data);
        }
        this.setIsloading(true);
        return this.myCostAccountingResource.getCostAccountingList(costAccountingTypeId, from, to)
            .pipe(
                tap((costAccountings: ICostAccounting[]) => this.updateState({ ..._state, data: costAccountings })),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }

    public async fetchCostTypeCosts(costAccountingId: number): Promise<void> {
        this.setIsloading(true);
        const costTypeCosts: ICostTypeCost[] = await firstValueFrom(this.myCostAccountingResource.getCostTypeCosts(costAccountingId)).catch(() => []);
        this.updateState({ ..._state, costTypeCosts });
        this.setIsloading(false);
    }

    public async initializeCostAccounting(addCostAccounting: IAddCostAccounting): Promise<ICostAccounting> {
        this.setIsloading(true);
        const newCostAccounting: ICostAccounting = await firstValueFrom(this.myCostAccountingResource.initializeCostAccounting(addCostAccounting)).catch(() => null);
        if (newCostAccounting !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-accounting.actions.added.label');
        }
        this.setIsloading(false);
        return newCostAccounting;
    }

    public async continueCostAccounting(costAccountingId: number): Promise<ICalculationResult> {
        this.setIsloading(true);
        const result: ICalculationResult = await firstValueFrom(this.myCostAccountingResource.continueCostAccounting(costAccountingId)).catch(() => null);
        if (result !== null && !this.hasCalculationErrors(result.errors)) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-accounting.actions.continue.success.label');
        } else {
            this.myMessageService.errorTrans('app.actions.feedback.error', 'app.page.cost-accounting.actions.continue.error.label');
        }
        this.setIsloading(false);
        return result;
    }

    public async importIndirectCost(costAccountingId: number, indirectCost: IIndirectCostImport[]): Promise<ICostAccounting> {
        this.setIsloading(true);
        const updatedCostAccounting: ICostAccounting = await firstValueFrom(this.myCostAccountingResource.importCostCenterCosts(costAccountingId, indirectCost)).catch(() => null);
        if (updatedCostAccounting !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-accounting.actions.continue.success.label');
        }
        this.setIsloading(false);
        return updatedCostAccounting;
    }

    public async setFallbackOptions(costAccountingId: number, fallbackOptions: IFallbackOptions): Promise<ICostAccounting> {
        this.setIsloading(true);
        const updatedCostAccounting: ICostAccounting = await firstValueFrom(this.myCostAccountingResource.setFallbackOptions(costAccountingId, fallbackOptions)).catch(() => null);
        this.setIsloading(false);
        return updatedCostAccounting;

    }

    public fetchCostAccountingTypes(): Observable<ICostAccountingType[]> {
        if (_state.costAccountingTypes.length > 0) {
            return of(_state.costAccountingTypes);
        }
        this.setIsloading(true);
        return this.myCostAccountingTypeResource.getAllCostAccountingTypes()
            .pipe(
                tap((costAccountingTypes: ICostAccountingType[]) => this.updateState({ ..._state, costAccountingTypes })),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }

    public clearCostTypeCosts(): void {
        this.updateState({ ..._state, costTypeCosts: [] });
    }

    public hasCalculationErrors(errors: IErrorCheck): boolean {
        return errors.missingPurchasePrices.length > 0
            || errors.missingPlannedPurchasePrices.length > 0
            || errors.missingMovingAveragePrices.length > 0
            || errors.missingByProductRevenues.length > 0
            || errors.missingCostCenterWages.length > 0;
    }

    private updateState(state: ICostAccountingState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }
}
